<script>
import Layout from '@/router/layouts/main';

import Repositories from '@/repositories';
const AllocationGroupsRepository = Repositories.get('allocationgroups');

import AllocationGroupsAdd from './components/add-form';
import PaginationGrid from '@/components/widgets/grids/api-linked-grid';
import DeleteButton from '@/components/widgets/buttons/delete-button';

export default {
  page: {
    title: 'AllocationGroups',
  },
  components: {
    Layout,
    AllocationGroupsAdd,
    PaginationGrid,
    DeleteButton,
  },
  data() {
    return {
      allocationGroupsList: [],
      gridContext: null,
      isLoading: false,
      fullListSize: 0,
    };
  },
  created() {
    this.gridContext = {
      currentPage: 1,
      perPage: this.itemsPerPage,
    };
    this.loadData();
  },
  methods: {
    loadData() {
      this.isLoading = true;
      AllocationGroupsRepository.getAll(this.gridContext)
        .then(({ data }) => {
          this.allocationGroupsList = data.data.map((data) => {
            return this.addExtraFields(data);
          });
          this.fullListSize = data.count;
        })
        .catch(() => {
          this.$toast.errorToast(this.$t('errors.errorLoading'));
        })
        .finally(() => {
          this.isLoading = false;
        });
    },
    addExtraFields(allocationGroup) {
      allocationGroup.editActive = false;
      allocationGroup.nameEdit = allocationGroup.name;
      allocationGroup.isLoading = false;
      allocationGroup.editError = false;
      allocationGroup.deleteError = false;
      return allocationGroup;
    },
    allocationGroupAdded() {
      this.loadData();
    },
    beginEdit(allocation) {
      allocation.editActive = true;
      this.$nextTick(() =>
        this.$refs[`alloc-grp-input-${allocation.idPublic}`].focus()
      );
    },
    cancelEdit(allocation) {
      allocation.editActive = false;
    },
    submitEdit(allocation) {
      allocation.isLoading = true;

      let queryModel = {
        idPublic: allocation.idPublic,
        name: allocation.nameEdit,
      };

      AllocationGroupsRepository.update(queryModel, allocation.idPublic)
        .then(() => {
          this.loadData();
        })
        .catch(() => {
          allocation.isLoading = false;
          allocation.editError = true;
          this.$toast.errorToast(this.$t('errors.errorLoading'));
        });
    },
    deleteAllocationGroup(allocationGroup) {
      allocationGroup.isLoading = true;

      AllocationGroupsRepository.delete(allocationGroup.idPublic)
        .then(() => {
          this.gridContext.currentPage = 1;
          this.loadData();
        })
        .catch(() => {
          allocationGroup.deleteError = true;
          allocationGroup.isLoading = false;
          this.$toast.errorToast(this.$t('errors.errorLoading'));
        });
    },
    gridContextChanged(context) {
      this.gridContext = context;
      this.loadData();
    },
  },
  computed: {
    fieldsComputed() {
      return [
        {
          key: 'name',
          label: this.$t('general.name'),
          class: 'table-row-expandable',
          sortable: true,
        },
        {
          key: 'edit',
          label: this.$t('general.edit'),
          class: 'table-row-relative',
        },
        {
          key: 'delete',
          label: this.$t('general.delete'),
          class: 'table-row-relative',
        },
      ];
    },
    itemsPerPage() {
      return this.$store.getters['localstorage/getDynamicConfigs'](
        'gridConfig-allocation-group-grid'
      ).itemsPerPage;
    },
  },
};
</script>

<template>
  <Layout :title="$t('allocations.allocationGroups')">
    <div style="margin-left: -12px">
      <AllocationGroupsAdd @allocation-group-added="allocationGroupAdded" />
    </div>

    <PaginationGrid
      id="allocation-group-grid"
      :items="allocationGroupsList"
      :fields="fieldsComputed"
      :current-page="gridContext.currentPage"
      :is-busy="isLoading"
      :nbItems="fullListSize"
      @context-changed="gridContextChanged"
    >
      <template #cell(name)="data">
        <b-form-input
          v-if="data.item.editActive"
          v-model="data.item.nameEdit"
          @keydown.enter.prevent="submitEdit(data.item)"
          @keydown.escape.prevent="cancelEdit(data.item)"
          :ref="`alloc-grp-input-${data.item.idPublic}`"
        />
        <div v-else>{{ data.item.name }}</div>
      </template>
      <template #cell(edit)="data">
        <b-button
          v-if="data.item.editActive"
          :disabled="data.item.isLoading"
          @click="submitEdit(data.item)"
          @keydown.escape.prevent="cancelEdit(data.item)"
          type="button"
          block
          variant="primary"
        >
          <i
            v-if="data.item.isLoading"
            class="bx bx-loader-alt bx-spin"
            style="font-size: 1.2em"
          >
          </i>
          <i
            v-else-if="data.item.editError"
            class="fas fa-exclamation text-danger"
            style="margin-left: 4px; margin-right: 4px"
          ></i>
          <span v-else>
            {{ $t('general.edit') }}
          </span>
        </b-button>
        <i
          v-else
          class="fas fa-pencil-alt text-success pl-2 pr-2"
          @click="beginEdit(data.item)"
          style="cursor: pointer; font-size: 1.3em"
        ></i>
      </template>
      <template #cell(delete)="data">
        <b-button
          v-if="data.item.editActive"
          :disabled="data.item.isLoading"
          @click="cancelEdit(data.item)"
          @keydown.escape.prevent="cancelEdit(data.item)"
          type="button"
          block
          variant="primary"
        >
          {{ $t('general.cancel') }}
        </b-button>
        <DeleteButton
          v-else
          :key="data.item.idPublic"
          :error="!data.item.isLoading && data.item.deleteError"
          @clicked="deleteAllocationGroup(data.item)"
        />
      </template>
    </PaginationGrid>
  </Layout>
</template>
